








































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Api from '@/project/http/Api.service';
import urlHelper, { UrlFacets } from '@/core/urlHelper.service';
import { Route } from 'vue-router';
import {
    CategoryTreeViewModel,
    FacetsViewModel,
    MenuItem,
    SearchRequest,
    SearchViewModel
} from '@/types/apiServerContract';
import { HttpCancelError } from '@/core/http/HttpCancelError';
import { debounce } from 'lodash';
import gtmService from '@/project/tracking/gtm/GTM.service';
import SearchResultPanel from './SearchResultPanel.vue';
import { PRODUCT_SEARCH_COUNT } from './constants';
import { BaseContentModel, BreadcrumbItem, JsonContent, PageData } from '@/types/contentServerContract';
import appStore from '@/core/app/app.store';
import contentApiService from '@/core/http/contentApi.service';
import domService from '@/core/dom/dom.service';
import loggingService from '@/core/logging.service';
import serverContextService from '@/core/serverContext.service';
import facetSortService from '@/project/search/facet.sort.service';

@Component({
    components: {
        SearchResultPanel
    }
})
export default class SearchProductControl extends Vue {
    @Prop({ type: String, required: true })
    private contextKey!: string;

    @Prop({ type: Number, default: PRODUCT_SEARCH_COUNT })
    private pageSize!: number;

    @Prop({ type: String, default: '' })
    private campaignCode!: string;

    @Prop({ type: String, default: '' })
    private brandName!: string;

    @Prop({ type: String })
    private cableType!: string;

    @Prop({ type: String })
    private cableEndA!: string;

    @Prop({ type: String })
    private cableEndB!: string;

    @Prop({ type: String })
    private categoryId!: string;

    @Prop({ type: Boolean })
    private withResultsBreadcrumb!: boolean;

    @Prop({ type: Boolean })
    private includePageDataBreadcrumb!: boolean;

    @Prop({ type: String, default: '' })
    private categoryUrlBase!: string;

    @Prop({ type: String })
    private pageDataUrl!: string;

    @Prop()
    private allowedBrands!: string[];

    @Prop({ type: Boolean, default: false })
    private renderMetaData!: boolean;

    @Prop({ type: Boolean, default: false })
    private trackPageView!: boolean;

    @Prop({ type: Boolean, default: false })
    private useFlattenedCategoryTree!: boolean;

    @Prop({ type: Boolean, default: false })
    private allowTerm!: boolean;

    @Prop({ type: Boolean, default: true })
    private allowSearch!: boolean;

    @Prop({ type: Boolean, default: false })
    private reactiveCategories!: boolean;

    @Prop({ type: Function })
    private searchResultCallback!: ((searchResult: SearchViewModel) => void) | undefined;

    @Prop({ type: Function })
    private pageDataResultCallback!: ((pageData?: PageData) => void) | undefined;

    @Prop({ type: Boolean, default: false })
    private showBlocksAndProducts!: boolean;

    private intialized: boolean = false;
    private pageBlocks: JsonContent[] | null = null;
    private pageBlocksBelow: JsonContent[] | null = null;
    private pageBreadcrumbs: BreadcrumbItem[] | null = null;
    private showAllProducts: boolean = false;
    private pageTracked: boolean = false;

    private searchResult: SearchViewModel | null = null;
    private facetsResult: FacetsViewModel | null = null;

    private menuItems: readonly MenuItem[] = [];
    private activeMenu: MenuItem | null = null;
    private rootMenuItem: MenuItem | null = null;

    private term = '';
    private translatedTerm: string | null = null;

    private searching = false;
    private debouncedTrackSearch = debounce(this.trackSearch, 2000)

    private getCategoryId() {
        return this.categoryId || urlHelper.getCategoryId();
    }

    private updateTerm() {
        this.term = this.allowTerm ? (urlHelper.getTerm() || '') : '';
    }

    private setSearching(isSearching: boolean) {
        this.searching = isSearching;
    }

    private resetSelectedMenus() {
        this.menuItems.forEach(x => x.selected = false);
    }

    private createSearchRequest() {
        const request: SearchRequest = {
            categoryId: this.getCategoryId(),
            categoryIds: urlHelper.getCategoryIds(),
            term: this.translatedTerm || this.term,
            lastFacet: urlHelper.getLastFacet() || '',
            page: urlHelper.getPage(),
            pageSize: this.pageSize,
            filter: urlHelper.getSearchFilterId(),
            priceSort: urlHelper.getSearchPriceSortFilter(),
            campaignCode: this.campaignCode || urlHelper.getCampaignCode() || '',
            brandName: this.brandName,
            cableType: this.cableType,
            cableEndTypeA: this.cableEndA,
            cableEndTypeB: this.cableEndB,
            showScore: urlHelper.getShowScore(),
            wIds: [],
            explain: urlHelper.getShowExplanation(),
            useFlattenedCategoryTree: this.useFlattenedCategoryTree,
            isExternalProductGuide: window.servercontext.isExternalProductGuide,
            showInStockFilter: !(serverContextService.externalProductGuideSettings?.hideStock ?? false),
            allowedBrands: this.allowedBrands || undefined
        };

        return request;
    }

    private createUrlFacets(): UrlFacets {
        return facetSortService.createUrlFacets(this.facetsResult);
    }

    private async search() {
        this.updateTerm();

        // Category-id is either from category (when on category-page) or from url if in search-mode
        const request: SearchRequest = this.createSearchRequest();
        const facets: UrlFacets = this.createUrlFacets();

        // If parent disallows search
        if (!this.allowSearch) return;
        // Only search if category or term or campaign
        if (!request.categoryId && !request.term && !request.campaignCode && !request.brandName && (!request.cableEndTypeA && !request.cableEndTypeB)) return;
        // Do not search on category or campaign page if search is active, unless first time
        if (this.term && !this.allowTerm) return;
        // Do not search on search overlay if no term has been applied
        if (!this.term && this.allowTerm) return;

        this.setSearching(true);
        try {
            // Make parallel calls and handle individual responses as soon as they arrive - but handle errors in common way.
            await Promise.all([this.searchCall(request, facets), this.facetCall(request, facets), this.categoryCall(request, facets)]);

            if (this.searchResult?.translation) {
                this.translatedTerm = this.searchResult?.translation?.translation;
            }

            this.setSearching(false);
            this.intialized = true;
        } catch (error) {
            if (error instanceof HttpCancelError) {
                // Ok - cancelled by newer request
            } else {
                this.setSearching(false);
                this.intialized = true;
                throw error;
            }
        }

        if (this.term) {
            this.debouncedTrackSearch();
        }
    }

    private async searchCall(request: SearchRequest, facets: UrlFacets) {
        const result = await Api.search.search(request, facets, this.contextKey);
        this.searchResult = result;

        if (this.searchResultCallback) {
            this.searchResultCallback(result);
        }
    }

    private async facetCall(request: SearchRequest, facets: UrlFacets) {
        const result = await Api.search.facets(request, facets, this.contextKey);
        this.facetsResult = result;
        this.setupFacets(result);
    }

    private setupFacets(result: FacetsViewModel) {
        const facetsToKeep = [...result.facets];
        if (result.filters) {
            facetsToKeep.push(result.filters);
        }
        if (result.sorting) {
            facetsToKeep.push(result.sorting);
        }
        urlHelper.cleanupUrl(facetsToKeep.map(x => ({
            key: x.facetId,
            activeValueKeys: x.values.filter(x => x.selected).map(x => x.key),
            from: x.rangeFrom,
            to: x.rangeTo
        })));
    }

    private async categoryCall(request: SearchRequest, facets: UrlFacets) {
        const result: CategoryTreeViewModel = await Api.search.categories(request, facets, this.contextKey);

        if (this.categoryUrlBase) {
            fixupUrls(result.menuItems, this.categoryUrlBase);
            fixupUrls(result.breadcrumb, this.categoryUrlBase);
        }

        this.menuItems = this.reactiveCategories
            ? result.menuItems
            : Object.freeze(result.menuItems);

        this.activeMenu = result.selectedMenu ?? null;
        this.rootMenuItem = result.rootMenu ?? null;

        this.setupBreadcrumb(result.breadcrumb);

        function fixupUrls(menus: MenuItem[], path: string): void {
            for (const menu of menus) {
                menu.url = path + '/' + menu.url;
                fixupUrls(menu.children, path);
            }
        }
    }

    private setupBreadcrumb(megaMenuPath: readonly MenuItem[]) {
        if (!this.includePageDataBreadcrumb && !this.withResultsBreadcrumb) return;

        let result: MenuItem[] = [];

        // On a campaign page we want to prepend the breadcrumb with the path to the campaigns content page
        if (this.includePageDataBreadcrumb && this.pageBreadcrumbs) {
            result = this.pageBreadcrumbs as MenuItem[];
        }

        if (this.withResultsBreadcrumb) {
            result = [...result, ...megaMenuPath];
        }

        if (result.length > 0) {
            appStore.setBreadcrumb(result);
        }
    }

    private async getPageData() {
        const pageUrl = this.pageDataUrl;
        if (!pageUrl || urlHelper.getCategoryId()) {
            this.pageBlocks = null;
            this.pageBlocksBelow = null;
            this.pageBreadcrumbs = null;
            this.trackPage();
            return;
        }

        try {
            const pageData = await contentApiService.getPageParallel(pageUrl);
            if (this.pageDataResultCallback) {
                this.pageDataResultCallback(pageData);
            }

            if (this.renderMetaData) {
                const isRelativeUrl = this.brandName !== '';
                domService.updateMetaData(pageData.metadata, isRelativeUrl);
            }

            this.trackPage(pageData.metadata.id);
            this.setPageBlocks(pageData);
            this.setPageBlocksBelow(pageData);
            this.setPageBreadcrumbs(pageData);
            this.setShowAllProducts(pageData);
        } catch {
            this.pageBlocks = null;
            this.pageBlocksBelow = null;
            this.pageBreadcrumbs = null;

            if (this.pageDataResultCallback) {
                this.pageDataResultCallback();
            }
        }
    }

    private setPageBlocks(pageData: PageData) {
        this.pageBlocks = (pageData?.jsonContent?.content as BaseContentModel).blocks ?? null;
    }

    private setPageBlocksBelow(pageData: PageData) {
        this.pageBlocksBelow = (pageData?.jsonContent?.content as BaseContentModel).blocksBelow ?? null;
    }

    private setPageBreadcrumbs(pageData: PageData) {
        this.pageBreadcrumbs = (pageData?.navigation?.breadcrumb) ?? null;
    }

    private setShowAllProducts(pageData: PageData) {
        this.showAllProducts = (pageData?.jsonContent?.content.listAllProducts as boolean) ?? false;
    }

    private async setAlternativeUrlLanguages() {
        if (!this.categoryId) { return; }

        try {
            const result = await Api.metadata.getAlternativeCategoryUrls(this.categoryId);
            if (result.alternativeUrls.length > 1) {
                domService.setHreflang(result.alternativeUrls);
            }
        } catch (error) {
            loggingService.error(error);
        }
    }

    private trackSearch() {
        gtmService.trackSearch(this.term, this.searchResult?.totalHits ?? 0);
    }

    private trackPage(pageId?: number) {
        if (this.trackPageView && !this.pageTracked && this.$route.name) {
            this.pageTracked = true;
            gtmService.trackGenericPageView(this.$route.name, pageId);
        }
    }

    private get hideAdminOptions(): boolean {
        return !!(this.cableType || this.cableEndA || this.cableEndB);
    }

    @Watch('$route')
    onRouteChange(to: Route, from?: Route) {
        // If user engaged in a new search, remove the replaced term
        if (to.query.term !== from?.query.term) {
            this.translatedTerm = null;
            this.resetSelectedMenus();
            urlHelper.clearFacets();
            urlHelper.removeQueryParams('pageKey', 'categoryIdKey', 'categoryIdsKey');
        } else if (to.params.categoryId !== from.params.categoryId) {
            urlHelper.removeQueryParams('pageKey');
        }

        // Always allow it to render at page load, in case we are on a page while also having an active term
        if (this.intialized) {
            const isSearching = urlHelper.isSearching(to) || urlHelper.isSearching(from);
            if (isSearching && !this.allowTerm) return;
        }

        this.search();
    }

    @Watch('pageDataUrl')
    onPageDataUrlChange() {
        this.pageTracked = false;
        this.getPageData();
    }

    @Watch('allowSearch')
    onAllowSearchChange() {
        if (this.allowSearch) {
            this.search();
        }
    }

    @Watch('categoryId', {
        immediate: true
    })
    onCategoryIdChange() {
        this.setAlternativeUrlLanguages();
    }

    mounted() {
        this.getPageData().then(() => {
            this.search();
        });
    }
}
